// SPDX-License-Identifier: GPL-3.0-or-later
// The timed events of a run: the hurry-up jackpot, the frenzy and the combo chain.
// Pure: no I/O, no clock. Time comes in as game ms (dt).
// Each event returns the points to pay. This module never pays.
// The rules are in the run design document, section 5.
import { RUN_LIMITS } from "./perks.ts";
import type { TimedEventKind, TimedEventState } from "./types.ts";

/** Hurry-up value at the start of the timer. */
export const HURRY_START_VALUE = 100_000;
/** Hurry-up value at the end of the timer. */
export const HURRY_END_VALUE = 25_000;
/** Bumper hits that start a frenzy. */
export const FRENZY_HITS = 8;
/** Window for the frenzy hits, in game ms. */
export const FRENZY_WINDOW_MS = 5_000;
/** Points per chain step. The award is this value times (length - 1). */
export const CHAIN_STEP_POINTS = 10_000;
/** Chain award cap. */
export const CHAIN_MAX_POINTS = 40_000;

/** Clears a timed state. The state stays one object. */
function clearTimed(s: TimedEventState): void {
  s.active = false;
  s.msLeft = 0;
  s.totalMs = 0;
  s.value = 0;
  s.count = 0;
}

/** The award for a chain of this length. 0 below length 2. */
export function chainAward(length: number): number {
  if (length < 2) return 0;
  return Math.min(CHAIN_STEP_POINTS * (length - 1), CHAIN_MAX_POINTS);
}

/** A fresh, inactive state for one timed event kind. */
export function newTimedState(kind: TimedEventKind): TimedEventState {
  return { kind, active: false, msLeft: 0, totalMs: 0, value: 0, count: 0 };
}

/** The hurry-up jackpot. Its value falls in a straight line during the timer. */
export class HurryUp {
  constructor(readonly state: TimedEventState) {}

  /** Starts the timer. Returns false when a hurry-up already runs. The caller checks the phase. */
  start(bonusMs: number): boolean {
    const s = this.state;
    if (s.active) return false;
    const total = Math.min(RUN_LIMITS.HURRY_MS + bonusMs, RUN_LIMITS.HURRY_MAX_MS);
    s.active = true;
    s.totalMs = total;
    s.msLeft = total;
    s.value = HURRY_START_VALUE;
    return true;
  }

  /**
   * Moves the timer on. Returns true when the timer ends now. A fail has no penalty.
   * A dt that is not above 0, NaN or Infinity does nothing.
   */
  tick(dtMs: number): boolean {
    if (!(dtMs > 0) || !Number.isFinite(dtMs)) return false;
    const s = this.state;
    if (!s.active) return false;
    s.msLeft -= dtMs;
    if (s.msLeft <= 0) {
      clearTimed(s);
      return true;
    }
    const elapsed = s.totalMs - s.msLeft;
    const raw = HURRY_START_VALUE - ((HURRY_START_VALUE - HURRY_END_VALUE) * elapsed) / s.totalMs;
    s.value = Math.floor(raw / 1_000) * 1_000;
    return false;
  }

  /** Collects the jackpot. Returns the points now. Returns 0 when no hurry-up runs. */
  collect(): number {
    const s = this.state;
    if (!s.active) return 0;
    const points = s.value;
    clearTimed(s);
    return points;
  }

  /** Ends the hurry-up with no points. Use at ball end and at game start. */
  end(): void {
    clearTimed(this.state);
  }
}

/**
 * The frenzy. Eight bumper hits in 5 s start a timer of doubled switch value.
 * A hit is in the window when its age is at most FRENZY_WINDOW_MS. The edge is inclusive.
 */
export class Frenzy {
  /** Game time of each recent hit. Preallocated, so a hit does not allocate. */
  private readonly stamps = new Float64Array(FRENZY_HITS);
  /** Ring index of the next write. After a full ring it is also the oldest entry. */
  private next = 0;
  /** Number of valid entries in the ring. */
  private filled = 0;
  /** Game ms since the run started. Only `tick` moves it. */
  private clock = 0;

  constructor(readonly state: TimedEventState) {}

  /** A bumper hit. Returns true when the hit starts a frenzy. Hits during a frenzy do not count. */
  hit(bonusMs: number): boolean {
    const s = this.state;
    if (s.active) return false;
    this.stamps[this.next] = this.clock;
    this.next = (this.next + 1) % FRENZY_HITS;
    this.filled = Math.min(this.filled + 1, FRENZY_HITS);
    s.count = this.hitsInWindow();
    if (s.count < FRENZY_HITS) return false;
    this.startTimer(bonusMs);
    return true;
  }

  /**
   * Moves the clock and the timer on. Returns true when the frenzy ends now.
   * A dt that is not above 0, NaN or Infinity does nothing.
   */
  tick(dtMs: number): boolean {
    if (!(dtMs > 0) || !Number.isFinite(dtMs)) return false;
    const s = this.state;
    this.clock += dtMs;
    if (!s.active) {
      if (s.count > 0) s.count = this.hitsInWindow();
      return false;
    }
    s.msLeft -= dtMs;
    if (s.msLeft <= 0) {
      clearTimed(s);
      this.filled = 0;
      this.next = 0;
      return true;
    }
    return false;
  }

  /** Ends the frenzy and clears the ring. Use at ball end and at game start. */
  end(): void {
    clearTimed(this.state);
    this.filled = 0;
    this.next = 0;
  }

  /** Does the same as `end`. Also sets the clock to 0. Use at the start of a run. */
  reset(): void {
    this.end();
    this.clock = 0;
  }

  /** Hits in the last FRENZY_WINDOW_MS. Scans at most FRENZY_HITS entries. */
  private hitsInWindow(): number {
    let n = 0;
    for (let i = 0; i < this.filled; i++) {
      if (this.clock - (this.stamps[i] ?? -Infinity) <= FRENZY_WINDOW_MS) n++;
    }
    return n;
  }

  /** Starts the timer and clears the ring, so a new frenzy needs new hits. */
  private startTimer(bonusMs: number): void {
    const s = this.state;
    const total = Math.min(RUN_LIMITS.FRENZY_MS + bonusMs, RUN_LIMITS.FRENZY_MAX_MS);
    s.active = true;
    s.totalMs = total;
    s.msLeft = total;
    s.value = 0;
    s.count = 0;
    this.filled = 0;
    this.next = 0;
  }
}

/** The combo chain. Each shot opens a window. Each combo award adds 1 to the chain. */
export class ComboChain {
  constructor(readonly state: TimedEventState) {}

  /** A ramp or orbit shot. Opens the window, or restarts it when open. */
  shot(windowMs: number): void {
    const s = this.state;
    s.active = true;
    s.totalMs = windowMs;
    s.msLeft = windowMs;
  }

  /**
   * A combo award. Adds 1 to the chain and returns the award for the new length.
   * Returns 0 when no window is open. Chain length 1 pays 0.
   */
  combo(): number {
    const s = this.state;
    if (!s.active) return 0;
    s.count += 1;
    s.value = chainAward(s.count + 1);
    return chainAward(s.count);
  }

  /**
   * Moves the window on. Returns true when the window ends now. The chain goes to 0.
   * A dt that is not above 0, NaN or Infinity does nothing.
   */
  tick(dtMs: number): boolean {
    if (!(dtMs > 0) || !Number.isFinite(dtMs)) return false;
    const s = this.state;
    if (!s.active) return false;
    s.msLeft -= dtMs;
    if (s.msLeft <= 0) {
      clearTimed(s);
      return true;
    }
    return false;
  }

  /** Ends the chain window and the chain. Use at ball end and at game start. */
  end(): void {
    clearTimed(this.state);
  }
}
